import { SafeResourceUrl } from "@angular/platform-browser";
import { Timestamp } from "firebase/firestore";

/**
 * String id of the Fender Studio Pro community — every historical `community_posts` doc
 * implicitly belongs to it. Also the value the frontend writes to `community_string_id` on
 * every new post authored from the public community (i.e. anywhere the collection is
 * `community_posts` rather than `beta_community_posts`). Mirrored in paeapi at
 * `src/constants/communities.ts`.
 */
export const FSP_COMMUNITY_STRING_ID = "fender-studio-pro" as const;

/**
 * Build the relative path (no host, no leading slash — caller adds the base URL) to a
 * community post detail page. Rules:
 *
 *   - beta post → `beta/betapost/:id?betaStringId=…&reply=…?` (unchanged behavior)
 *   - any community (including FSP) → `community/:communityStringId/post/:id[?reply=…]`
 *
 * As of 2026-08-31 FSP uses the same URL shape as every other community. The legacy
 * `community/post/:id` shape still resolves (via redirect stubs in
 * `apps/web-fender/.../community-page.module.ts`) so inbound links / bookmarks keep working.
 *
 * Keeps share URLs, notification URLs, and SEO canonicals in sync everywhere.
 */
export function buildCommunityPostPath(opts: { postId: string; communityStringId?: string; betaStringId?: string; replyId?: string }): string {
  const { postId, communityStringId, betaStringId, replyId } = opts;
  if (betaStringId) {
    let path = `beta/betapost/${postId}?betaStringId=${betaStringId}`;
    if (replyId) path += `&reply=${replyId}`;
    return path;
  }
  const community = communityStringId || FSP_COMMUNITY_STRING_ID;
  const suffix = replyId ? `?reply=${replyId}` : "";
  return `community/${community}/post/${postId}${suffix}`;
}

/**
 * Per-community feature flags. Read from `communities/{string_id}.features` and applied by
 * shared UI components (compose editor, view modes, filter bar, etc.). Multi-community
 * expansion — DENG-727 Phase 3. Mirror of paeapi's CommunityFeatures type.
 */
export interface CommunityFeatures {
  subject_required: boolean;
  mentions_enabled: boolean;
  scheduled_posts: boolean;
  editor: "quill" | "contenteditable";
  announcements: boolean;
  view_modes: Array<"list" | "detailed">;
}

/** Optional branding surface — hero image, accent, welcome copy. */
export interface CommunityBranding {
  hero_image_url?: string | null;
  /**
   * Square image for lists (the hub rail, suggestions, the channel explorer). Uploaded in the
   * admin editor beside the hero; lists fall back to the hero when there's none.
   */
  icon_url?: string | null;
  /**
   * Theme applied to the hero section. "light" = light theme (dark text on the image);
   * "dark" = dark theme (light text). Applied as `theme_plainlight` / `theme_plaindark`
   * on the hero container so every themed rule (text color, borders, pill styling)
   * resolves correctly regardless of the viewer's global theme. Null = inherit.
   */
  hero_theme?: "light" | "dark" | null;
  accent_color?: string | null;
  welcome_html?: string | null;
  empty_state_html?: string | null;
}

/**
 * Communities are always publicly readable. `participation_condition` gates POSTING:
 *
 * - `none` — any authenticated user can post (default for new communities)
 * - `fsp_plus_subscriber` — active Fender Studio Pro+ subscription
 * - `fender_play_subscriber` — active Fender Play subscription
 * - `product_owner` — owns at least one product from `requiredProducts`
 * - `group_member` — is in at least one MySQL group from `requiredGroups`
 *
 * Enforcement TBD (frontend compose form + paeapi post-write path). Mirror of
 * paeapi's `CommunityParticipationCondition`.
 */
export type CommunityParticipationCondition = "none" | "fsp_plus_subscriber" | "fender_play_subscriber" | "product_owner" | "group_member";

/**
 * A single `requiredProducts` entry. Tagged by identifier type because Fender products
 * are addressable by SKU or productNo (FMIC Products API) while PreSonus products are
 * addressable by `string_id` (paeapi `Product` table). The admin picker fires the right
 * lookup and stores the resolved shape here so consumers don't repeat the resolution.
 * `label` is a display-name snapshot; fall back to `value` when absent.
 */
export interface ProductRequirement {
  type: "sku" | "productNo" | "string_id";
  value: string;
  label?: string;
  /**
   * Product image URL captured at pick time — persisted so the frontend "you need
   * this product to post" prompt can show a real thumbnail without re-fetching from
   * FMIC / Product table. Optional; fall back to a placeholder when absent.
   */
  image?: string;
}

/**
 * Firestore `communities/{string_id}` doc shape. Fetched into CommunitiesService on app
 * boot; shared components read the config to determine editor / subject-required /
 * mentions / view modes / etc. per community.
 */
/** A paeapi environment (`ENVIRONMENT` on the API). */
export type CommunityEnvironment = "dev" | "qa" | "prod";

export interface Community {
  id: string;
  string_id: string;
  title: string;
  /**
   * Short per-locale description shown under the community title in the layout header.
   * `{ en, es, fr, ... }` map so admins can auto-translate via `POST /admin/translate`
   * without a code deploy. Resolve via `resolveLocalizedString(community.description, lang)`.
   */
  description?: LocalizedString | string | null;
  /**
   * Who can POST in this community. Communities themselves are always publicly readable.
   */
  participation_condition: CommunityParticipationCondition;
  /** MySQL group names. Used when `participation_condition === "group_member"`. */
  requiredGroups?: string[] | null;
  /** Product string_ids. Used when `participation_condition === "product_owner"`. */
  requiredProducts?: ProductRequirement[] | null;
  admin_only?: boolean;
  hidden?: boolean;
  /**
   * Employees only: left out of the community list for everyone else (`CommunitiesService`),
   * so a community can be switched on in prod and tried by staff first. Visibility, not
   * access control — like `hidden`, its posts are still readable to anyone with the link.
   */
  employee_only?: boolean;
  /**
   * Which paeapi environments serve this community. Absent or empty = every one. paeapi
   * leaves the community out of `GET /communities` (and 404s it) elsewhere, so a new
   * community can be tried in dev or QA without appearing in prod — the Firestore
   * collection is shared by every environment.
   */
  environments?: CommunityEnvironment[] | null;
  deleted?: boolean;
  created?: unknown;
  created_by?: string;
  order?: number;
  /** MySQL group names — any user in these groups is a moderator of this community. */
  moderator_roles: string[];
  /** MySQL group names — any user in these groups is an admin of this community. */
  admin_roles: string[];
  features: CommunityFeatures;
  branding?: CommunityBranding;
}

/**
 * DynamoDB item in `community_channels` — per-community channel taxonomy. Fetched by
 * `GET /communities/{string_id}/channels`. `title` and `description` are per-locale maps
 * (`{ en, es, ... }`) so admins can auto-translate without a code deploy. Resolve the
 * display value via `resolveLocalizedString(channel.title, currentLang)`.
 */
export type LocalizedString = Record<string, string>;
export interface CommunityChannel {
  community_string_id: string;
  string_id: string;
  title: LocalizedString | string;
  description?: LocalizedString | string | null;
  is_public?: boolean;
  is_alphabetical?: boolean;
  role?: string | null;
  /**
   * Restricts who may start a post in this channel: MySQL group names, any one of which
   * qualifies. Empty / absent = anyone who passes the community's participation gate.
   * Replies are never restricted — only parent posts. Community moderators and admins
   * always qualify. See `canPostInChannel`.
   */
  posting_groups?: string[] | null;
  ordering?: number;
  archived?: boolean;
  created?: string;
}

/** A user's MySQL group names, from `user.groups` (entries are either names or `{ name }`). */
export function userGroupNames(user: any): string[] {
  return Array.isArray(user?.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : [];
}

/**
 * Whether a user may start a post (not a reply) in `channel`. True for an unrestricted
 * channel, for `isModerator` (community moderator/admin — the caller resolves that, see
 * `CommunitiesService.canPostInChannel`), and for a member of any of `posting_groups`.
 * Frontend enforcement, same standing as `computeParticipationGate`.
 */
export function canPostInChannel(channel: Pick<CommunityChannel, "posting_groups"> | null | undefined, userGroups: string[] | undefined, isModerator: boolean): boolean {
  const allowed = channel?.posting_groups;
  if (!allowed?.length) return true;
  if (isModerator) return true;
  return isInAnyRequiredGroup(allowed, userGroups);
}

/**
 * Pick the best-fit string from a per-locale map, falling back to English then the raw
 * value. Handles both the map shape and legacy bare-string values that predate DENG-728's
 * localization migration.
 */
export function resolveLocalizedString(value: LocalizedString | string | null | undefined, lang: string, fallback = ""): string {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return value[lang] || value.en || Object.values(value)[0] || fallback;
}

/**
 * Reasons a user might fail the participation gate. Consumed by both `<myp-create-post-input>`
 * (compose) and `<myp-comment>` (reply) to decide which upgrade / warning prompt to show.
 * See `computeParticipationGate` below for the full rule table.
 *
 * `product_owner_todo` and `group_member_todo` are placeholders — the corresponding
 * conditions currently fail-open (user is allowed to participate) because enforcement
 * hasn't shipped yet. The reason is still surfaced so callers can log / hide UI hints if
 * they want, but no upgrade prompt is shown for those two.
 */
export type ParticipationGateReason = "signed_out" | "profile_not_public" | "needs_fsp_plus" | "needs_fender_play" | "needs_product_ownership" | "needs_group_membership";

export interface ParticipationGate {
  /** True when the current user is allowed to post / reply in this community. */
  ok: boolean;
  /**
   * When `ok` is false, why. When `ok` is true, this may still hold a `_todo` reason
   * (product/group deferred enforcement) — callers should ignore it in that case.
   */
  reason: ParticipationGateReason | null;
}

/**
 * Single source of truth for "can this user participate in this community". Pure —
 * everything it needs comes in via `opts` so it's trivially testable and shareable
 * between the compose and reply flows without a DI dance.
 *
 * Rule table:
 *
 *   - user is null                                                    → { ok:false, reason:"signed_out" }
 *   - community.participation_condition = "none"                      → any authenticated user (default for new communities)
 *   - community.participation_condition = "fsp_plus_subscriber"       → user.active_subscription
 *   - community.participation_condition = "fender_play_subscriber"    → subDetails.play_subscription_normalized.status === "active"
 *   - community.participation_condition = "product_owner"             → user owns at least one product in requiredProducts
 *   - community.participation_condition = "group_member"              → user is in at least one group in requiredGroups
 *   - condition met but `profilePublic` is false                      → { ok:false, reason:"profile_not_public" }
 *   - all checks pass                                                 → { ok:true, reason:null }
 *
 * When the community config isn't cached yet (`community` is undefined), the default
 * condition is `none` — matches the admin-form default for new communities.
 */
/**
 * Communities that have a full onboarding-wizard flow. Adding a string_id here turns the
 * wizard on for that community on BOTH platforms, so each also needs its own step-3 content
 * — `@if (communityStringId == "...")` in the web wizard template, and the matching branch in
 * the NativeScript onboarding modal.
 *
 * Lives here rather than beside either implementation so the two cannot drift: a community
 * that onboards on the web and not in the app is a support ticket waiting to happen.
 *
 * Rendering is gated by this registry AND the per-community setup-complete flag on the user's
 * profile (`hasSetupCommunities[stringId]`) — someone who has run one community's flow still
 * sees another's on first visit.
 */
const COMMUNITIES_WITH_ONBOARDING_FLOW: ReadonlySet<string> = new Set([FSP_COMMUNITY_STRING_ID, "tone-master-pro"]);

export function communityHasOnboardingFlow(stringId: string): boolean {
  return !!stringId && COMMUNITIES_WITH_ONBOARDING_FLOW.has(stringId);
}

/**
 * Whether a signed-in member gets the communities hub's onboarding (web `<myp-communities-
 * onboarding>`, ns-myfender `<myf-hub-onboarding>`): never finished it, and not already part
 * of the communities some other way. `profile` is the raw `user_profiles` doc, or `false` when
 * there is none yet — a brand-new account.
 *
 * "Already part of it" is anything the per-community wizards or a join left behind, so nobody
 * who has been here before is walked through it: a follow, a finished Fender Studio Pro /
 * Tone Master Pro wizard, or the activation date that wizard wrote. Shared so both platforms
 * agree on who is new.
 */
export function needsCommunityOnboarding(profile: any): boolean {
  if (!profile) return true;
  if (profile.communityOnboarded) return false;
  if (profile.hasSetupCommunity || profile.dateCommunityActivated) return false;
  if (Object.values(profile.hasSetupCommunities || {}).some(Boolean)) return false;
  return !Object.values(profile.followingCommunities || {}).some(Boolean);
}

export function computeParticipationGate(opts: {
  community: Community | undefined;
  user: any;
  profilePublic: boolean;
  subDetails?: any;
  /** User's MySQL group names — read from `userService.user.groups`. Only consulted when `participation_condition === "group_member"`. */
  userGroups?: string[];
  /**
   * Ownership predicate — returns true if the user owns a product satisfying this
   * requirement. Callers implement it against whatever product store they have (typically
   * `ProductService.ownsProductRequirement`). Kept as a callback so the gate helper stays
   * pure and doesn't need to know about ProductService's internal maps. Only consulted
   * when `participation_condition === "product_owner"`.
   *
   * IMPORTANT: callers must wait until their product list has actually loaded before
   * invoking the gate — an unresolved product cache always reports "doesn't own" and
   * would flash the wrong prompt to a user who does qualify. See the compose/reply
   * components for the `products$` wait pattern.
   */
  ownsProduct?: (req: ProductRequirement) => boolean;
}): ParticipationGate {
  const { community, user, profilePublic, subDetails, userGroups, ownsProduct } = opts;
  if (!user) return { ok: false, reason: "signed_out" };

  const condition: CommunityParticipationCondition = community?.participation_condition || "none";

  let meets = false;
  let unmet: ParticipationGateReason = "needs_fsp_plus";
  switch (condition) {
    case "none":
      meets = true;
      break;
    case "fsp_plus_subscriber":
      meets = !!user.active_subscription;
      unmet = "needs_fsp_plus";
      break;
    case "fender_play_subscriber":
      meets = subDetails?.play_subscription_normalized?.status === "active";
      unmet = "needs_fender_play";
      break;
    case "product_owner":
      meets = ownsAnyRequiredProduct(community?.requiredProducts, ownsProduct);
      unmet = "needs_product_ownership";
      break;
    case "group_member":
      meets = isInAnyRequiredGroup(community?.requiredGroups, userGroups);
      unmet = "needs_group_membership";
      break;
  }

  if (!meets) return { ok: false, reason: unmet };
  if (!profilePublic) return { ok: false, reason: "profile_not_public" };
  return { ok: true, reason: null };
}

/**
 * True if the user is a member of ANY group listed in `requiredGroups`. If the community
 * has no `requiredGroups` (or an empty list), the config is misconfigured and we fail
 * closed — an admin should either add groups or switch the participation condition.
 */
function isInAnyRequiredGroup(requiredGroups: string[] | null | undefined, userGroups: string[] | undefined): boolean {
  if (!requiredGroups?.length) return false;
  if (!userGroups?.length) return false;
  const owned = new Set(userGroups);
  return requiredGroups.some((g) => owned.has(g));
}

/**
 * True if the user owns ANY product listed in `requiredProducts`. Delegates the actual
 * matching to the caller-supplied `ownsProduct` callback — the gate helper doesn't need
 * to know about `ProductService`'s internal maps or paeapi's Product row shape.
 *
 * Fail closed when either input is missing:
 *   - No `requiredProducts` → admin chose `product_owner` but selected nothing. Misconfigured.
 *   - No `ownsProduct` callback → caller forgot to plumb it in. Better to block than to leak.
 */
function ownsAnyRequiredProduct(requiredProducts: ProductRequirement[] | null | undefined, ownsProduct: ((req: ProductRequirement) => boolean) | undefined): boolean {
  if (!requiredProducts?.length) return false;
  if (!ownsProduct) return false;
  return requiredProducts.some((req) => ownsProduct(req));
}

// This is mostly for documentation, as it isn't being strictly checked in implementation.
export type SpotifyEmbedType = "track" | "album" | "artist" | "episode" | "show" | "playlist";

export interface MessageEmbed {
  type: "youtube" | "soundcloud" | "spotify";
  subtype?: SpotifyEmbedType;
  id: string;
  pastedUrl: string;
  embedSrc: SafeResourceUrl;
}

export type ConnectionRequestResponse = "accepted" | "rejected";

// Discord account link (Phase 0). Returned by GET /oauth/discord/link and optionally mirrored to
// the Firestore user_profiles doc as `discord_link` for a linked badge.
export interface DiscordLink {
  discordId: string;
  discordUsername?: string;
  linkedAt?: string;
  verified?: boolean;
  status?: string;
}

export interface UserConnection {
  created: Date;
  email: string;
  firstName?: string;
  id: string;
  initiated_by: string;
  initiated_by_fc_id?: string | null;
  lastName?: string;
  message?: string;
  photoURL?: string;
  removed_by: string;
  requested_user_id: string;
  requested_user_fc_id?: string | null;
  status: string;
  theirID: string;
  their_fc_id?: string | null;
}

export interface Thread {
  archived?: Record<string, Timestamp>[];
  connection: UserConnection;
  created: Timestamp;
  id: string;
  lastMessageTime?: Timestamp;
  missedMessages?: Record<string, number>[];
  them: string;
  users: string[];
  viewing?: string[];
}

export interface MessageAuthor {
  id: string;
  name: string;
  photo?: string;
}

export interface ThreadMessage {
  author: MessageAuthor;
  file?: any;
  message: string;
  timestamp: Timestamp;
  type: "message";
}

/**
 * A community's `branding.accent_color`, or null when it isn't a usable CSS color. Admins type
 * it in, so anything else (a missing "#", stray text) is dropped rather than written into a
 * style. One check for every surface that paints with it.
 */
export function sanitizeAccentColor(value: string | null | undefined): string | null {
  const accent = String(value || "").trim();
  return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\))$/i.test(accent) ? accent : null;
}
